import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getUsers, updateUserStatus, getUserProfile, UserListResult } from '../services/adminService';
import { cacheRead, cacheWrite, cacheInvalidate, cacheAge } from '../utils/dataCache';
import { 
  pageContainer, headerStyle, breadcrumb, titleStyle, subtitleStyle, 
  statsGrid, cardStyle, labelStyle, valueStyle, sectionTitle,
  primaryBtn, secondaryBtn, tableSearch, selectStyle, thStyle, tdStyle, badgeStyle
} from '../styles/theme';
import ProfileModal from '../components/ProfileModal';
import ReasonModal from '../components/ReasonModal';
import { exportToCSV } from '../utils/csvExport';
import { getUserRole, getUserDepartment } from '../utils/auth';

// ── Cache config ───────────────────────────────────────────────
const CACHE_NS     = 'users:';
const HARD_TTL_MS  = 5 * 60 * 1000; // 5 min — don't show cached data older than this
const REVALIDATE_MS = 60 * 1000;     // 60 s — silently revalidate if older than this

// ── Debounce hook ─────────────────────────────────────────────
function useDebounce<T>(value: T, delayMs: number): T {
  const [dv, setDv] = useState<T>(value);
  useEffect(() => {
    const t = setTimeout(() => setDv(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return dv;
}

// ── Pagination component ──────────────────────────────────────
const PAGE_SIZE = 15;

interface PaginatorProps { total: number; page: number; onPage: (p: number) => void; }
const Paginator: React.FC<PaginatorProps> = ({ total, page, onPage }) => {
  const pages = Math.ceil(total / PAGE_SIZE);
  if (pages <= 1) return null;
  const start = Math.max(1, page - 2);
  const end   = Math.min(pages, page + 2);
  const nums: number[] = [];
  for (let i = start; i <= end; i++) nums.push(i);

  const btn = (label: string | number, target: number, active = false, disabled = false) => (
    <button key={label} onClick={() => !disabled && onPage(target)} style={{
      padding: "6px 12px", borderRadius: 8, border: "1px solid #e2e8f0",
      background: active ? "#2563eb" : disabled ? "#f8fafc" : "#fff",
      color: active ? "#fff" : disabled ? "#cbd5e1" : "#475569",
      fontWeight: active ? 700 : 500, fontSize: 13,
      cursor: disabled ? "default" : "pointer", transition: "all .15s",
    }}>{label}</button>
  );

  return (
    <div style={{ display: "flex", gap: 6, alignItems: "center", justifyContent: "flex-end", marginTop: 16 }}>
      <span style={{ fontSize: 12, color: "#94a3b8", marginRight: 8 }}>
        Page {page} of {pages} ({total} results)
      </span>
      {btn("‹", page - 1, false, page === 1)}
      {start > 1 && <>{btn(1, 1)}{start > 2 && <span style={{ color: "#94a3b8" }}>…</span>}</>}
      {nums.map((n) => btn(n, n, n === page))}
      {end < pages && <>{end < pages - 1 && <span style={{ color: "#94a3b8" }}>…</span>}{btn(pages, pages)}</>}
      {btn("›", page + 1, false, page === pages)}
    </div>
  );
};

// ── Helpers ────────────────────────────────────────────────────
const EMPTY_RESULT: UserListResult = {
  items: [], summary: { total: 0, active: 0, suspended: 0, newToday: 0 },
};

function formatAge(ms: number): string {
  if (ms < 60_000) return `${Math.round(ms / 1000)}s ago`;
  return `${Math.round(ms / 60_000)}m ago`;
}

// ── Main component ────────────────────────────────────────────
const Users = () => {
  const role = getUserRole();
  const department = getUserDepartment();
  const isSupport = role === "SUPPORT" || department === "SUPPORT";

  const [result, setResult]           = useState<UserListResult>(EMPTY_RESULT);
  const [loading, setLoading]         = useState(true);
  const [revalidating, setRevalidating] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState<number | null>(null);
  const [syncLabel, setSyncLabel]     = useState('');

  const [searchInput,  setSearchInput]  = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const debouncedSearch = useDebounce(searchInput, 350);
  const [page, setPage] = useState(1);

  const [profileOpen, setProfileOpen]       = useState(false);
  const [profileData, setProfileData]       = useState<any>(null);
  const [profileLoading, setProfileLoading] = useState(false);

  const [suspendModalOpen, setSuspendModalOpen] = useState(false);
  const [suspendTarget, setSuspendTarget]     = useState<{ id: string; name: string } | null>(null);

  // Tick the "synced X ago" label every 15 s
  useEffect(() => {
    if (!lastSyncedAt) return;
    const tick = () => setSyncLabel(formatAge(Date.now() - lastSyncedAt));
    tick();
    const id = setInterval(tick, 15_000);
    return () => clearInterval(id);
  }, [lastSyncedAt]);

  // ── Core fetch (supports silent background revalidation) ────
  const doFetch = useCallback(async (silent = false) => {
    const filters = { search: debouncedSearch || undefined, status: filterStatus || undefined };
    const key = `${CACHE_NS}${debouncedSearch}:${filterStatus}`;

    if (!silent) setLoading(true);
    else setRevalidating(true);

    const data = await getUsers(filters);
    cacheWrite(key, data);
    setResult(data);
    setLastSyncedAt(Date.now());
    setSyncLabel('just now');
    setPage(1);

    if (!silent) setLoading(false);
    else setRevalidating(false);
  }, [debouncedSearch, filterStatus]);

  // ── On filter change or mount: try cache first ──────────────
  const fetchUsers = useCallback(async () => {
    const key = `${CACHE_NS}${debouncedSearch}:${filterStatus}`;
    const cached = cacheRead<UserListResult>(key, HARD_TTL_MS);

    if (cached) {
      // Show cached data instantly — no spinner
      setResult(cached);
      setLoading(false);
      const age = cacheAge(key) ?? 0;
      setLastSyncedAt(Date.now() - age);

      // Background revalidate only if the entry is getting stale
      if (age > REVALIDATE_MS) {
        doFetch(true /* silent */);
      }
      return;
    }

    // No usable cache — full fetch with loading spinner
    doFetch(false);
  }, [debouncedSearch, filterStatus, doFetch]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // ── Mutations: invalidate cache then hard refetch ────────────
  const handleToggleSuspend = async (userId: string, currentlySuspended: boolean, name: string) => {
    if (currentlySuspended) {
      // Direct activation
      if (!window.confirm(`Are you sure you want to activate user "${name}"?`)) return;
      await performUpdate(userId, false);
    } else {
      // Open suspension modal
      setSuspendTarget({ id: userId, name });
      setSuspendModalOpen(true);
    }
  };

  const handleSuspendConfirm = async (reason: string) => {
    if (!suspendTarget) return;
    const { id } = suspendTarget;
    setSuspendModalOpen(false);
    await performUpdate(id, true, reason);
    setSuspendTarget(null);
  };

  const performUpdate = async (userId: string, shouldSuspend: boolean, reason?: string) => {
    try {
      await updateUserStatus(userId, shouldSuspend, reason);
      cacheInvalidate(CACHE_NS);
      doFetch(false);
    } catch {
      alert("Failed to update user status");
    }
  };

  const handleViewProfile = async (userId: string) => {
    setProfileOpen(true);
    setProfileLoading(true);
    setProfileData(null);
    const data = await getUserProfile(userId);
    setProfileData(data);
    setProfileLoading(false);
  };

  const handleExportCSV = () => {
    const exportData = result.items.map(u => ({
      Name: u.name || 'Anonymous',
      Email: u.email || 'N/A',
      Phone: u.phone || 'N/A',
      Role: u.role,
      Status: u.isSuspended ? 'Suspended' : 'Active',
      CreatedAt: new Date(u.createdAt).toLocaleDateString(),
    }));
    exportToCSV(exportData, 'Users_Report');
  };

  const { items, summary } = result;
  const paginated = useMemo(
    () => items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [items, page],
  );

  const stats = [
    { label: "Total Users",  val: summary.total.toString(),     sub: "Registered users",  color: "#2563eb" },
    { label: "Active",       val: summary.active.toString(),    sub: "Healthy accounts",  color: "#10b981" },
    { label: "Suspended",    val: summary.suspended.toString(), sub: "Flagged/Banned",    color: "#ef4444" },
    { label: "New Today",    val: summary.newToday.toString(),  sub: "Joined recently",   color: "#f59e0b" },
  ];

  return (
    <div style={pageContainer}>
      {/* Header */}
      <div style={headerStyle}>
        <div>
          <div style={breadcrumb}>Dashboard {'>'} Users</div>
          <h1 style={titleStyle}>User Management</h1>
          <p style={subtitleStyle}>Oversee all platform customers, monitor engagement, and manage account access.</p>
        </div>
        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          {/* Sync status */}
          {lastSyncedAt && !loading && (
            <span style={{
              fontSize: 11, color: revalidating ? "#f59e0b" : "#94a3b8",
              display: "flex", alignItems: "center", gap: 4,
            }}>
              {revalidating ? 'Syncing…' : `Synced ${syncLabel}`}
            </span>
          )}
          <button style={secondaryBtn} onClick={handleExportCSV}>Export CSV</button>
          <button
            style={primaryBtn}
            onClick={() => { cacheInvalidate(CACHE_NS); doFetch(false); }}
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Stats */}
      <div style={statsGrid}>
        {stats.map((s, i) => (
          <div key={i} style={cardStyle}>
            <div style={{ ...labelStyle, color: "#64748b" }}>{s.label}</div>
            <div style={{ ...valueStyle, color: s.color, margin: "8px 0" }}>{s.val}</div>
            <div style={{ fontSize: "12px", color: "#94a3b8" }}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div style={cardStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <h3 style={{ ...sectionTitle, margin: 0 }}>All Platform Users</h3>
            {!loading && <span style={{ fontSize: 12, color: "#94a3b8" }}>{items.length} results</span>}
          </div>
          <div style={{ display: "flex", gap: "12px" }}>
            <input
              type="text"
              placeholder="Search name, email or phone…"
              style={tableSearch}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <select
              style={selectStyle}
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">All Status</option>
              <option value="active">Active Only</option>
              <option value="suspended">Suspended Only</option>
            </select>
          </div>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
                <th style={thStyle}>USER DETAILS</th>
                <th style={thStyle}>CONTACT INFO</th>
                <th style={thStyle}>JOIN DATE</th>
                <th style={thStyle}>STATUS</th>
                <th style={{ ...thStyle, textAlign: "right" }}>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} style={{ padding: "40px", textAlign: "center", color: "#64748b" }}>
                  Loading users…
                </td></tr>
              ) : paginated.length === 0 ? (
                <tr><td colSpan={5} style={{ padding: "40px", textAlign: "center", color: "#94a3b8" }}>
                  No users match your current criteria.
                </td></tr>
              ) : paginated.map(user => (
                <tr
                  key={user.id}
                  style={{ borderBottom: "1px solid #f8fafc", cursor: "pointer", transition: "background .15s" }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "hsl(var(--muted) / 0.6)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                  onClick={() => handleViewProfile(user.id)}
                >
                  <td style={tdStyle}>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <div style={{
                        width: "36px", height: "36px",
                        background: "linear-gradient(135deg, #3b82f6, #8b5cf6)",
                        borderRadius: "50%", display: "flex", alignItems: "center",
                        justifyContent: "center", fontWeight: "700", color: "#fff", fontSize: "14px",
                      }}>
                        {(user.name || '?').charAt(0)}
                      </div>
                      <div style={{ fontWeight: "700", color: "hsl(var(--foreground))" }}>{user.name || 'Anonymous User'}</div>
                    </div>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ fontSize: "13px", color: "hsl(var(--foreground))" }}>{user.email || '–'}</div>
                    <div style={{ fontSize: "12px", color: "#94a3b8" }}>{user.phone || 'No phone'}</div>
                  </td>
                  <td style={tdStyle}>
                    {new Date(user.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                  </td>
                  <td style={tdStyle}>
                    <span style={{
                      ...badgeStyle,
                      background: !user.isSuspended ? "#dcfce7" : "#fee2e2",
                      color:      !user.isSuspended ? "#15803d" : "#b91c1c",
                    }}>
                      {!user.isSuspended ? 'Active' : 'Suspended'}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, textAlign: "right" }}>
                    {!isSupport && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleToggleSuspend(user.id, !!user.isSuspended, user.name || "Anonymous User"); }}
                        style={{
                          padding: "6px 12px", borderRadius: "6px",
                          background: user.isSuspended ? "#dcfce7" : "#fee2e2",
                          color:      user.isSuspended ? "#15803d" : "#b91c1c",
                          border: "none", fontWeight: "700", fontSize: "12px",
                          cursor: "pointer", marginRight: 8,
                        }}
                      >
                        {user.isSuspended ? 'Activate' : 'Suspend'}
                      </button>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); handleViewProfile(user.id); }}
                      style={{
                        padding: "6px 12px", borderRadius: "6px",
                        background: "#eff6ff", color: "#2563eb",
                        border: "none", fontWeight: "700", fontSize: "12px", cursor: "pointer",
                      }}
                    >
                      View Profile
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Paginator total={items.length} page={page} onPage={setPage} />
      </div>

      <ProfileModal
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        data={profileData}
        loading={profileLoading}
        type="user"
      />

      <ReasonModal
        open={suspendModalOpen}
        onClose={() => setSuspendModalOpen(false)}
        onConfirm={handleSuspendConfirm}
        title="Suspend User Account"
        description={`Are you sure you want to suspend ${suspendTarget?.name}'s account? This will restrict their access to the platform.`}
        confirmText="Suspend Account"
        placeholder="Reason for suspension (e.g. policy violation, suspicious activity)..."
      />
    </div>
  );
};

export default Users;
